/* iter-53n++ pass 6 — Community templates marketplace browse page.
 *
 * Public browse feed (no auth required to look). Buying requires sign-in
 * because the purchase debits the user's AI-tokens wallet (MOCK payment
 * — real Stripe wiring lands next iteration).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Store, Wand2, BookOpen, Users, DownloadCloud, Sparkles, Filter, ChevronLeft, Search, Flag, MoreVertical, ArrowDownAZ } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { marketplaceApi } from '@/lib/api';
import { useAuth } from '@/lib/auth_context';
import { useSeoMeta } from '@/lib/useSeoMeta';
import StructuredData, { breadcrumbLd, collectionPageLd } from '@/components/seo/StructuredData';

const SEO_FALLBACK = {
  title:       'Community Templates Marketplace — Crown & Rose',
  description: 'Browse author-crafted novel stencils — character bibles, beat sheets, chapter outlines — built by working indie authors. 85% of every sale goes to the author.',
  keywords:    'novel template marketplace, character bible template, chapter outline template, indie author marketplace, buy novel stencil',
};

const TIER_LABEL = { small: 'Small · $2', medium: 'Medium · $5', large: 'Large · $10' };
const SORT_LABEL = {
  newest:     'Newest',
  downloads:  'Most bought',
  price_asc:  'Price ↑',
  price_desc: 'Price ↓',
};
const REPORT_REASONS = [
  { value: 'spam',           label: 'Spam or low-quality' },
  { value: 'plagiarism',     label: 'Plagiarism or stolen work' },
  { value: 'inappropriate',  label: 'Inappropriate content' },
  { value: 'copyright',      label: 'Copyright violation' },
  { value: 'misleading',     label: 'Misleading description' },
  { value: 'other',          label: 'Other (describe below)' },
];

export default function MarketplacePage() {
  useSeoMeta('/marketplace', SEO_FALLBACK);
  const { user } = useAuth();
  const siteUrl  = typeof window !== 'undefined' ? window.location.origin : 'https://crownnrose.com';
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ genres: [], tiers: {} });
  const [tierFilter, setTierFilter] = useState('');
  const [genreFilter, setGenreFilter] = useState('');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sortMode, setSortMode] = useState('newest');
  const [buying, setBuying] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);

  // Debounce the search input so we don't hammer the API on every keystroke.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(h);
  }, [query]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortMode };
      if (tierFilter)     params.tier  = tierFilter;
      if (genreFilter)    params.genre = genreFilter;
      if (debouncedQuery) params.q     = debouncedQuery;
      const r = await marketplaceApi.browse(params);
      setItems(r.items || []);
      setFacets(r.facets || { genres: [], tiers: {} });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load marketplace.');
    } finally { setLoading(false); }
  }, [tierFilter, genreFilter, debouncedQuery, sortMode]);

  useEffect(() => { load(); }, [load]);

  const buy = async (t) => {
    if (!user) {
      toast.info('Sign in to purchase a template.');
      window.location.assign('/');
      return;
    }
    setBuying(t.id);
    try {
      const res = await marketplaceApi.purchase(t.id);
      if (res?.checkout_url) {
        window.location.assign(res.checkout_url);
        return;
      }
      toast.error('Could not start checkout — please try again.');
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Purchase failed.');
    } finally { setBuying(null); }
  };

  const totalGenres = useMemo(() => facets.genres || [], [facets]);

  return (
    <div className="min-h-screen bg-app-bg text-foreground" data-testid="marketplace-page">
      <StructuredData id="marketplace-breadcrumb" data={breadcrumbLd([
        { name: 'Home',        item: `${siteUrl}/` },
        { name: 'Marketplace', item: `${siteUrl}/marketplace` },
      ])} />
      <StructuredData id="marketplace-collection" data={collectionPageLd({ siteUrl })} />
      <header className="sticky top-0 z-10 border-b border-panel-border bg-app-bg/95 backdrop-blur px-6 py-3">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-3">
          <Button size="sm" variant="ghost" onClick={() => window.location.assign('/app')} data-testid="marketplace-back">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to workspace
          </Button>
          <h1 className="flex items-center gap-2 text-[15px] font-semibold">
            <Store className="h-4 w-4 text-primary" /> Community Templates
          </h1>
          <span className="hidden text-[11px] text-muted-foreground md:inline">
            · Author-crafted novel stencils. 85% of each sale goes to the author.
          </span>

          <div className="relative ml-auto flex items-center">
            <Search className="pointer-events-none absolute left-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title or author…"
              className="h-8 w-48 pl-7 text-[12px]"
              data-testid="marketplace-search-input"
            />
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 gap-1.5 text-[11.5px]" data-testid="marketplace-sort-btn">
                <ArrowDownAZ className="h-3.5 w-3.5" /> {SORT_LABEL[sortMode]}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(SORT_LABEL).map(([k, l]) => (
                <DropdownMenuItem key={k} onClick={() => setSortMode(k)} data-testid={`marketplace-sort-${k}`}>
                  {l}{sortMode === k ? ' ✓' : ''}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Chip strip — tiers + top genres */}
        <div className="mx-auto mt-2 flex max-w-6xl flex-wrap items-center gap-1.5">
          <Filter className="h-3.5 w-3.5 text-muted-foreground" />
          {['', 'small', 'medium', 'large'].map((t) => (
            <button
              key={t || 'all'}
              onClick={() => setTierFilter(t)}
              data-testid={`marketplace-filter-${t || 'all'}`}
              className={`rounded-full px-2.5 py-0.5 text-[11px] capitalize transition-colors ${tierFilter === t ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
            >
              {t ? `${TIER_LABEL[t]} ${facets.tiers?.[t] ? `(${facets.tiers[t]})` : ''}` : 'All tiers'}
            </button>
          ))}
          {totalGenres.length > 0 && (
            <>
              <span className="mx-1 h-3 w-px bg-panel-border" />
              <button
                onClick={() => setGenreFilter('')}
                data-testid="marketplace-genre-all"
                className={`rounded-full px-2.5 py-0.5 text-[11px] capitalize transition-colors ${!genreFilter ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:bg-secondary/60'}`}
              >
                All genres
              </button>
              {totalGenres.slice(0, 10).map((g) => (
                <button
                  key={g.genre}
                  onClick={() => setGenreFilter(g.genre === genreFilter ? '' : g.genre)}
                  data-testid={`marketplace-genre-${g.genre}`}
                  className={`rounded-full px-2.5 py-0.5 text-[11px] capitalize transition-colors ${genreFilter === g.genre ? 'bg-primary/85 text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
                >
                  {g.genre} <span className="opacity-60">({g.count})</span>
                </button>
              ))}
            </>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-[12px] text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading templates…
          </div>
        ) : items.length === 0 ? (
          <div className="mx-auto max-w-md rounded-md border border-dashed border-panel-border p-8 text-center text-[12.5px] text-muted-foreground" data-testid="marketplace-empty">
            <Sparkles className="mx-auto h-6 w-6 text-primary/60" />
            <p className="mt-2 font-semibold text-foreground">
              {debouncedQuery || genreFilter || tierFilter ? 'No matching templates' : 'No templates yet'}
            </p>
            <p className="mt-1">
              {debouncedQuery || genreFilter || tierFilter
                ? 'Try clearing filters or a different search.'
                : 'Check back soon, or publish your own from the Templates picker.'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map((t) => (
              <div
                key={t.id}
                data-testid={`marketplace-card-${t.id}`}
                className="group flex flex-col rounded-md border border-panel-border bg-card p-4 transition-all hover:border-primary/60 hover:shadow-panel"
              >
                <div className="flex items-baseline justify-between gap-2">
                  <h3 className="min-w-0 flex-1 truncate text-[14px] font-semibold" title={t.name}>{t.name}</h3>
                  <Badge className="shrink-0 font-mono-stat text-[10.5px]">
                    ${(t.price_cents / 100).toFixed(2)}
                  </Badge>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        className="rounded p-0.5 text-muted-foreground opacity-0 hover:bg-secondary hover:text-foreground group-hover:opacity-100"
                        data-testid={`marketplace-card-menu-${t.id}`}
                        aria-label="Card actions"
                      >
                        <MoreVertical className="h-3.5 w-3.5" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setReportTarget(t)} data-testid={`marketplace-report-${t.id}`}>
                        <Flag className="mr-2 h-3.5 w-3.5" /> Report listing
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {t.author && <span>by {t.author} · </span>}
                  {t.genre && <Badge variant="outline" className="mr-1 h-4 px-1.5 text-[9.5px] capitalize">{t.genre}</Badge>}
                  <span className="capitalize">{t.tier} tier</span>
                </div>

                <div className="mt-2 flex flex-wrap gap-3 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1"><BookOpen className="h-3 w-3 text-primary/70" /> {t.chapter_count} ch</span>
                  <span className="flex items-center gap-1"><Users className="h-3 w-3 text-primary/70" /> {t.character_count} chars</span>
                  <span className="flex items-center gap-1"><DownloadCloud className="h-3 w-3 text-primary/70" /> {t.downloads} bought</span>
                </div>

                {t.mood_tags?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.mood_tags.slice(0, 4).map((tag, i) => (
                      <span key={`${t.id}-tag-${i}`} className="rounded-full border border-primary/25 bg-primary/8 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary/85">{tag}</span>
                    ))}
                  </div>
                )}

                <div className="mt-auto pt-3">
                  <Button
                    size="sm"
                    onClick={() => buy(t)}
                    disabled={buying === t.id}
                    data-testid={`marketplace-buy-${t.id}`}
                    className="w-full gap-1.5 text-[11.5px]"
                  >
                    {buying === t.id
                      ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Purchasing…</>
                      : <><Wand2 className="h-3.5 w-3.5" /> Buy for ${(t.price_cents / 100).toFixed(2)}</>}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ReportDialog
        template={reportTarget}
        onClose={() => setReportTarget(null)}
        signedIn={!!user}
      />
    </div>
  );
}

function ReportDialog({ template, onClose, signedIn }) {
  const [reason, setReason] = useState('spam');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (template) { setReason('spam'); setDetails(''); }
  }, [template]);

  const submit = async () => {
    if (!signedIn) {
      toast.info('Sign in to report listings.');
      window.location.assign('/');
      return;
    }
    setSubmitting(true);
    try {
      await marketplaceApi.report(template.id, { reason, details });
      toast.success('Thanks — a moderator will review this listing.');
      onClose();
    } catch (e) {
      const s = e?.response?.status;
      if (s === 400) toast.error("You can't report your own listing.");
      else if (s === 429) toast.error('You have already reported this listing. A moderator will review it soon.');
      else toast.error(e?.response?.data?.detail || 'Could not submit report.');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!template} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="marketplace-report-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report this template</DialogTitle>
          <DialogDescription>
            Flag {template?.name || 'this listing'} for moderator review. Reports are confidential.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            {REPORT_REASONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setReason(r.value)}
                data-testid={`marketplace-report-reason-${r.value}`}
                className={`rounded-md border px-3 py-2 text-left text-[12px] transition-colors ${reason === r.value ? 'border-primary bg-primary/10 text-foreground' : 'border-panel-border text-muted-foreground hover:border-primary/40'}`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <Textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            placeholder="Optional details (max 800 chars)"
            maxLength={800}
            rows={3}
            data-testid="marketplace-report-details"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="marketplace-report-cancel">Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="marketplace-report-submit">
            {submitting ? <><Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Submitting…</> : 'Submit report'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

